/**
 * ============================================================================
 * useFilterOptions — store-aware hook over the pure selectFilterOptions.
 * ============================================================================
 *
 * Lives at the component layer (not features/) so imports keep pointing
 * DOWNWARD: components → features → types. The pure cascade logic is in
 * src/features/api/filterOptions.ts; this hook only wires the Redux store
 * (cached metrics responses — per-week AND whole-window entries — plus the
 * active filter state) into it.
 */

import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from 'src/app/store';
import { metricsApi } from 'src/features/api/metricsApi';
import {
  selectFilterOptions,
  selectMetricGroupOptions,
  selectMetricNameOptions,
  FilterOption,
} from 'src/features/api/filterOptions';
import { useGetMetricDefinitionsQuery } from 'src/features/api/metricDefinitionsApi';
import { useGetWeekNumbersQuery } from 'src/features/api/weekNumbersApi';
import { deepDivePeriodOptions } from 'src/features/api/durationRange';
import { GetMetricsOutput } from 'src/types/metricsApi.types';
import { STATIC_FILTER_OPTIONS } from 'src/config/filterConfig';
import { WBR_REVIEW_TYPE } from 'src/constants';

/**
 * Static options (Report Period, Metric Group, OTD threshold) + cascaded
 * data-driven dimension values from every fulfilled getMetrics cache entry,
 * narrowed by the active filters (never by a page-level network scope).
 */
export function useFilterOptions(): FilterOption[] {
  // Documented RTK Query APIs only (no reaching into internal state shape):
  // selectCachedArgsForQuery lists the args of every cached entry;
  // endpoints.<name>.select(arg) resolves each entry's data.
  //
  // BOTH metrics endpoints are read. The WBR pages fetch through
  // `getMetricsWeek` (one entry per reporting period, stitched by
  // useMetricsWindow), so reading only `getMetrics` found ZERO entries there
  // and every data-driven dropdown (Customer, Locale, Channel, Sub Channel,
  // Domain, DCR ID) rendered empty. `getMetrics` is still live on
  // ExecutionMetricsDashboard, so both are unioned — the cascade takes a list
  // of responses and does not care how many envelopes carry the rowset.
  const responses = useAppSelector(
    (state) => {
      const weekArgs = metricsApi.util.selectCachedArgsForQuery(
        state,
        'getMetricsWeek',
      );
      const windowArgs = metricsApi.util.selectCachedArgsForQuery(
        state,
        'getMetrics',
      );
      const data = weekArgs.map(
        (arg) => metricsApi.endpoints.getMetricsWeek.select(arg)(state).data,
      );
      windowArgs.forEach((arg) => {
        data.push(metricsApi.endpoints.getMetrics.select(arg)(state).data);
      });
      return data.filter(
        (entry): entry is GetMetricsOutput => entry !== undefined,
      );
    },
    // New array identity per call — compare by content (entries' data refs
    // are stable between fetches, so this is cheap).
    shallowEqual,
  );
  const filterQuery = useAppSelector((state) => state.dashboard.filterQuery);
  // The served dashboard being viewed (multi-dashboard R3.1).
  const dashboardId = useAppSelector(
    (state) => state.dashboard.activeDashboardId,
  );

  // Metric Name values come from the definitions CATALOG (names are
  // presentation metadata, not row data) — the same session-cached query the
  // pages already issue, so this is a cache hit, never an extra fetch.
  const { data: definitionsResponse } = useGetMetricDefinitionsQuery({
    dashboardId,
    reviewType: WBR_REVIEW_TYPE,
  });
  const definitions = definitionsResponse?.definitions;

  // Time Period values: the BOUNDED recent-week list (8 back + current + 4
  // ahead, mode-clamped) — checking a week fetches it, so the offering is the
  // load bound (deep-dive-periodical R4.3). A session cache hit on every data
  // page.
  const { data: weekData } = useGetWeekNumbersQuery();
  const isSnapshot = useAppSelector(
    (state) => state.dashboard.dataMode === 'snapshot',
  );

  // No page-level network scope (QuickSight parity: the demand-category
  // checkbox row shows/hides visuals, it does not cascade the filter
  // controls) — only the OTHER active tokens narrow each property's options.
  return useMemo(() => {
    const timePeriods = deepDivePeriodOptions(
      weekData?.weeks,
      weekData?.currentWeek,
      isSnapshot,
    ).map((value) => ({ propertyKey: 'timePeriod', value }));
    return [
      ...STATIC_FILTER_OPTIONS,
      ...selectFilterOptions(responses, { query: filterQuery }),
      ...selectMetricGroupOptions(definitions),
      ...selectMetricNameOptions(definitions, filterQuery),
      ...timePeriods,
    ];
  }, [responses, filterQuery, definitions, weekData, isSnapshot]);
}
